import React, { useCallback, useEffect, useState } from 'react';
import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonInput, IonItem, IonSpinner } from '@ionic/react';
import { QueuedEstimate, ServiceEstimate } from '../../../interfaces/ServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import { ACS_PATTERN, money, pct, shortDate } from './serviceFormat';
import './OfficeQueue.css';

interface Props {
  canAdmin: boolean;
  refreshKey: number;
  onOpen: (e: ServiceEstimate) => void;
  /** An estimate changed here (assigned or sent back). */
  onChanged: (e: ServiceEstimate) => void;
  notify: (msg: string) => void;
}

/** The office's ACS# queue (2.0): approve and assign the number, or send the estimate back with a note. */
const OfficeQueue: React.FC<Props> = ({ canAdmin, refreshKey, onOpen, onChanged, notify }) => {
  const [rows, setRows] = useState<QueuedEstimate[] | null>(null);
  const [error, setError] = useState('');
  const [acs, setAcs] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState('');

  const load = useCallback(() => {
    serviceApi.officeQueue().then((q) => { setRows(q); setError(''); })
      .catch((e) => { setError(e instanceof Error ? e.message : 'Could not load the queue.'); setRows([]); });
  }, []);
  useEffect(load, [load, refreshKey]);

  const assign = async (q: QueuedEstimate) => {
    const v = (q.acs_number || acs[q.id] || q.suggested_acs).trim().toUpperCase();
    if (!ACS_PATTERN.test(v)) { notify('Use the format ACS26-0001.'); return; }
    setBusy(q.id);
    try {
      const saved = await serviceApi.assignAcs(q.id, v);
      notify(`${saved.acs_number} assigned to ${saved.customer_detail.name}`);
      onChanged(saved);
      load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not assign the ACS#.');
      load(); // the suggested numbers may have moved on
    } finally {
      setBusy('');
    }
  };

  const sendBack = async (q: QueuedEstimate) => {
    const note = (notes[q.id] || '').trim();
    if (!note) { notify('Add a note so the tech knows what to change.'); return; }
    setBusy(q.id);
    try {
      const saved = await serviceApi.sendBack(q.id, note);
      notify(`Sent back to ${saved.prepared_by_name}`);
      onChanged(saved);
      load();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not send it back.');
    } finally {
      setBusy('');
    }
  };

  return (
    <IonCard>
      <IonCardHeader><IonCardTitle>ACS# queue</IonCardTitle></IonCardHeader>
      <IonCardContent className="office-queue">
        <div className={`office-access ${canAdmin ? 'ok' : 'flag'}`}>
          {canAdmin ? 'You can assign ACS numbers and change settings.' : 'View only. Only Admin and Director can assign ACS numbers or change settings.'}
        </div>
        <p className="office-help">Estimates waiting for an ACS#. Flagged ones need approval first. The number is filled in with the next free number for this year; confirm it or type another.</p>
        {rows === null && <IonSpinner />}
        {error && <p className="office-error">{error}</p>}
        {rows && rows.length === 0 && !error && <p className="office-help">Nothing waiting. Submitted estimates appear here.</p>}
        {rows?.map((q) => {
          const keep = !!q.acs_number;
          const review = q.review_reasons.length > 0;
          const meta = [shortDate(q.estimate_date), q.prepared_by_name, q.tax_location_label || 'No location', `margin ${pct(q.margin)}`,
            keep ? `resubmitted, keeps ${q.acs_number}` : ''].filter(Boolean).join(' · ');
          return (
            <div className="office-row" key={q.id}>
              <div className="office-row-head">
                <div><strong>{q.customer_detail.name}</strong><div className="office-meta">{meta}</div></div>
                <strong className="office-total">{money(q.total_price)}</strong>
              </div>
              {review && <div className="estimate-flag"><strong>Needs approval.</strong> {q.review_reasons.join(' ')}</div>}
              {q.location_differs && (
                <div className="estimate-flag"><strong>Check the tax location.</strong> The tech chose {q.tax_location_label}, which differs from this customer&apos;s usual location ({q.customer_detail.tax_location_label}).</div>
              )}
              {q.customer_detail.status === 'pending' && <p className="office-meta">New customer added from the field. Check the name for duplicates.</p>}
              <div className="office-row-actions">
                <IonItem className="office-acs" lines="full">
                  <IonInput aria-label="ACS number" value={keep ? q.acs_number : (acs[q.id] ?? q.suggested_acs)} readonly={keep || !canAdmin}
                    onIonInput={(e) => setAcs({ ...acs, [q.id]: String(e.detail.value ?? '') })} />
                </IonItem>
                <IonButton onClick={() => assign(q)} disabled={!canAdmin || busy === q.id}>{review ? 'Approve and assign' : keep ? 'Approve' : 'Assign ACS#'}</IonButton>
                <IonItem className="office-note" lines="full">
                  <IonInput aria-label="Note for the tech" placeholder="Note for the tech (if sending back)" value={notes[q.id] || ''} disabled={!canAdmin}
                    onIonInput={(e) => setNotes({ ...notes, [q.id]: String(e.detail.value ?? '') })} />
                </IonItem>
                <IonButton fill="outline" onClick={() => sendBack(q)} disabled={!canAdmin || busy === q.id}>Send back</IonButton>
                <IonButton fill="clear" onClick={() => onOpen(q)}>Open</IonButton>
              </div>
            </div>
          );
        })}
      </IonCardContent>
    </IonCard>
  );
};

export default OfficeQueue;
